import type { Position } from "replo-runtime/shared/types";
import type { LengthValueType } from "replo-runtime/shared/utils/units";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import * as React from "react";

import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import twMerge from "@replo/design-system/utils/twMerge";
import { Check, ChevronDown } from "lucide-react";
import * as ReactDOM from "react-dom";
import useMeasure from "react-use-measure";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import {
  CSS_LENGTH_TYPES,
  formatCommaSeparatedValues,
  isCursorOnFirstValue,
  parseCommaSeparatedValues,
  parseUnit,
} from "replo-runtime/shared/utils/units";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import { parseFloat, round } from "replo-utils/lib/math";
import { isNotNullish } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";
import { useEffectEvent } from "replo-utils/react/use-effect-event";

// #region Types and Exports

type InputOption = {
  label: string;
  value: string | null;
  isDisabled?: boolean;
  icon?: React.ReactNode;
};

type LengthInputModifierHotkey = "shiftKey" | "altKey";

// #endregion

// #region Inlined pure utility functions

type DraggingDirections = "negative" | "positive";
type Hotkey = "shiftKey" | "altKey" | null;

const getCursor = (
  draggingType?: "horizontal" | "vertical",
  draggingDirection?: DraggingDirections,
) => {
  if (draggingType === "horizontal") {
    switch (draggingDirection) {
      case "positive":
        return "e-resize";
      case "negative":
        return "w-resize";
      default:
        return "ew-resize";
    }
  } else if (draggingType === "vertical") {
    switch (draggingDirection) {
      case "positive":
        return "n-resize";
      case "negative":
        return "s-resize";
      default:
        return "ns-resize";
    }
  }
  return "inherit";
};

const getHotKey = (event: {
  altKey: boolean;
  shiftKey: boolean;
  key?: string;
}): Hotkey => {
  const { shiftKey, altKey, key } = event;
  if (shiftKey || key === "Shift") {
    return "shiftKey";
  } else if (altKey || key === "Alt") {
    return "altKey";
  }
  return null;
};

function clampMinMax<T extends string | number>(
  value: T,
  maxValue?: T,
  minValue?: T,
) {
  if (
    isNotNullish(maxValue) &&
    Number.parseInt(value.toString(), 10) > (maxValue as number)
  ) {
    return maxValue;
  } else if (
    isNotNullish(minValue) &&
    Number.parseInt(value.toString(), 10) < (minValue as number)
  ) {
    return minValue;
  }
  return value;
}

type MenuOption = {
  label: string;
  value: string | null;
};

function getDisplayValueFromMenuOptions({
  isRawInputFocused,
  inputValue,
  menuOptions,
  unitDefaults,
  field,
  defaultUnit,
  metrics,
  allowedNonUnitValues,
}: {
  isRawInputFocused: boolean;
  inputValue: string;
  menuOptions?: MenuOption[];
  unitDefaults?: { value: string | number; unit: string };
  field: string;
  defaultUnit: string;
  metrics?: string[];
  allowedNonUnitValues?: string[];
}): string | number {
  if (isRawInputFocused) {
    return inputValue;
  }

  const matchingLabelOption = menuOptions?.find(
    (option) => option.label === inputValue,
  );
  if (matchingLabelOption) {
    return matchingLabelOption.label;
  }

  const parsed = parseUnit(
    inputValue,
    unitDefaults ?? { value: "", unit: "" },
    field,
    defaultUnit,
    undefined,
    metrics,
    allowedNonUnitValues,
  );

  if (
    allowedNonUnitValues?.includes(String(parsed.value)) &&
    typeof parsed.value === "string"
  ) {
    return parsed.value.charAt(0).toUpperCase() + parsed.value.slice(1);
  }

  const matchingOption = menuOptions?.find(
    (option) => String(option.value) === String(parsed.value),
  );
  return matchingOption?.label ?? `${parsed.value}${parsed.unit}`;
}

/**
 * NOTE (Andrew, 2026-01-09, REPL-20714): Normalize user input to include px units.
 * "24, 20" -> "24px, 20px", "30px, 15" -> "30px, 15px"
 * Returns null if not a comma-separated value pattern.
 */
function normalizeCommaSeparatedInput(value: string): string | null {
  const match = value
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*(?:px)?\s*,\s*(-?\d+(?:\.\d+)?)\s*(?:px)?$/i);
  if (!match) {
    return null;
  }
  return `${match[1]}px, ${match[2]}px`;
}

// #endregion

// #region Internal hooks

function useOverridableRef<T>(value: T) {
  const ref = React.useRef<T>(value);

  React.useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
}

// #endregion

// #region Component types

type DragTrigger = "label" | "startEnhancer" | "none";

type LengthInputBounds = Record<string, number>;

type CommonProps = {
  onDragEnd?(): void;
  onDragStart?(): void;
  onChange(value: any, hotkey: LengthInputModifierHotkey | null): void;
  transformValue?(value: any): any;
  startEnhancer?: React.ReactNode;
  endEnhancer?(): React.ReactNode;
  label?: React.ReactNode;
  field: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  style?: React.CSSProperties;
  className?: string;
  inputClassName?: string;
  backgroundColor?: string;
  placeholder?: string;
  draggingType?: "horizontal" | "vertical";
  draggingDirection?: DraggingDirections;
  dragTrigger?: DragTrigger;
  metrics?: string[];
  unitDefaults?: LengthValueType;
  allowsNegativeValue?: boolean;
  resetValue?: string | null;
  anchorValue?: string;
  menuOptions?: InputOption[];
  maxValues?: LengthInputBounds;
  minValues?: LengthInputBounds;
  maxDragValues?: LengthInputBounds;
  minDragValues?: LengthInputBounds;
  autofocus?: boolean;
  allowedNonUnitValues?: string[];
  value?: string | ReploMixedStyleValue | null;
  dragSpeed?: number;
};

type LengthInputSelectorProps = CommonProps;

// #endregion

const PARSE_UNIT_DEFAULTS = { value: "", unit: "" };

// #region Context

type LengthInputSelectorContextType = {
  onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  dragTrigger: CommonProps["dragTrigger"];
  draggingType: CommonProps["draggingType"];
  draggingDirection: CommonProps["draggingDirection"];
  field: CommonProps["field"];
  isDisabled: boolean;
  isReadOnly: boolean;
  onSubmit: (args: {
    value: string | null;
    currentlyPressedHotkey: LengthInputModifierHotkey | null;
    shouldBlur: boolean;
  }) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  setInputValue: (value: string) => void;
  parseValue: (value: string | number, unit?: string) => string;
  isMouseDown: boolean;
  metrics: CommonProps["metrics"];
  allowedNonUnitValues: CommonProps["allowedNonUnitValues"];
  unitDefaults: CommonProps["unitDefaults"];
  maxValues: CommonProps["maxValues"];
  minValues: CommonProps["minValues"];
  menuOptions: CommonProps["menuOptions"];
  defaultUnit: string;
  currentUnit: string;
  anchorValue: CommonProps["anchorValue"];
  isFocused: boolean;
  setIsFocused: (value: boolean) => void;
  isDragging: boolean;
  isCurrentlySubmittingValueRef: React.RefObject<boolean>;
  resetValue: () => void;
  isMixedStyleValue: boolean;
};
const LengthInputSelectorContext =
  React.createContext<LengthInputSelectorContextType | null>(null);

// #endregion

// #region Root component

type LengthInputSelectorPropsNeededForInput =
  | "startEnhancer"
  | "endEnhancer"
  | "placeholder"
  | "autofocus"
  | "backgroundColor";

const LengthInputSelectorRoot: React.FC<
  React.PropsWithChildren<
    Omit<LengthInputSelectorProps, LengthInputSelectorPropsNeededForInput>
  >
> = (props) => {
  const internalInputRef = React.useRef<HTMLInputElement | null>(null);
  const inputRef: React.RefObject<HTMLInputElement> =
    props.inputRef ?? internalInputRef;

  const defaultUnit = props.metrics?.[0] ?? "px";

  const nullValueMenuOption = props.menuOptions?.find(
    (opt) => opt.value === null,
  );
  const shouldDisplayMenuOptionLabel =
    props.value === null && nullValueMenuOption !== undefined;

  const { value: initialValue, unit: currentUnit } = parseUnit(
    !isMixedStyleValue(props.value) ? props.value ?? null : null,
    props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
    props.field,
    defaultUnit,
    undefined,
    props.metrics,
    props.allowedNonUnitValues,
  );

  const firstInputValue = shouldDisplayMenuOptionLabel
    ? nullValueMenuOption.label
    : `${initialValue}${currentUnit}`;
  const resolvedInputValue =
    firstInputValue === props.resetValue ? "" : firstInputValue;
  const currentValueRef = useOverridableRef(resolvedInputValue);

  const [inputValue, setInputValue] = useOverridableState(
    !isMixedStyleValue(props.value) ? resolvedInputValue : "Mixed",
  );
  const [lastDragStart, setLastDragStart] = React.useState<Position | null>(
    null,
  );
  const dragTargetIsFirstValue = React.useRef<boolean>(true);
  const [isMouseDown, setIsMouseDown] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const isCurrentlySubmittingValueRef = React.useRef(false);
  const [dragCursor, setDragCursor] = React.useState<string | null>(null);

  const {
    draggingType = "vertical",
    draggingDirection,
    maxValues,
    minValues,
    maxDragValues,
    minDragValues,
  } = props;

  const shouldAllowNegativeValue = props.allowsNegativeValue ?? true;

  const parseValue = (value: string | number, unit?: string) => {
    if (Number.isNaN(Number.parseFloat(`${value}`))) {
      return `${clampMinMax(
        value,
        maxValues?.[currentUnit],
        minValues?.[currentUnit],
      )}`;
    }

    const { unit: parsedUnit } = parseUnit(
      String(value),
      props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
      props.field,
      defaultUnit,
      undefined,
      props.metrics,
      props.allowedNonUnitValues,
    );

    let unitToApply =
      parsedUnit ?? currentUnit ?? props.unitDefaults?.unit ?? "px";

    if (unit) {
      unitToApply = unit;
    }

    return `${clampMinMax(
      Number.parseFloat(`${value}`),
      maxValues?.[currentUnit],
      minValues?.[currentUnit],
    )}${unitToApply}`;
  };

  const onSubmit = (args: {
    value: string | null;
    currentlyPressedHotkey: LengthInputModifierHotkey | null;
    shouldBlur: boolean;
  }) => {
    const { value, currentlyPressedHotkey: hotkey, shouldBlur } = args;

    if (value === null) {
      const nullValueMenuOption = props.menuOptions?.find(
        (opt) => opt.value === null,
      );
      const menuOptionLabel = nullValueMenuOption?.label ?? "";
      currentValueRef.current = menuOptionLabel;
      props.onChange(value, hotkey);
      if (shouldBlur) {
        isCurrentlySubmittingValueRef.current = true;
        inputRef.current?.blur();
        isCurrentlySubmittingValueRef.current = false;
      }
      return;
    }

    if (parseCommaSeparatedValues(value)) {
      currentValueRef.current = value;
      setInputValue(value);
      if (shouldBlur) {
        setIsFocused(false);
      }
      props.onChange(value, hotkey);
      if (shouldBlur) {
        isCurrentlySubmittingValueRef.current = true;
        inputRef.current?.blur();
        isCurrentlySubmittingValueRef.current = false;
      }
      return;
    }

    const tokens = parseUnit(
      value,
      props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
      props.field,
      defaultUnit,
      props.resetValue,
      props.metrics,
      props.allowedNonUnitValues,
    );

    if (
      !shouldAllowNegativeValue &&
      Number.parseFloat(String(tokens.value)) < 0
    ) {
      tokens.value = 0;
    }

    let newInputValue = parseValue(tokens.value, tokens.unit);

    if (props.transformValue) {
      newInputValue = props.transformValue(newInputValue);
    }

    setInputValue(newInputValue);
    if (shouldBlur) {
      setIsFocused(false);
    }

    if (
      newInputValue === currentValueRef.current ||
      (currentValueRef.current &&
        currentValueRef.current === props.resetValue) ||
      (!currentValueRef.current &&
        (newInputValue === props.resetValue ||
          parseFloat(newInputValue) === parseFloat(props.resetValue!)))
    ) {
      return;
    }
    currentValueRef.current = newInputValue;
    props.onChange(newInputValue, hotkey);
    if (shouldBlur) {
      isCurrentlySubmittingValueRef.current = true;
      inputRef.current?.blur();
      isCurrentlySubmittingValueRef.current = false;
    }
  };

  const shouldInputDrag =
    Boolean(draggingType || draggingDirection) && !props.isReadOnly;

  const resetValueFn = () => {
    const { resetValue } = props;
    if (resetValue === undefined) {
      return;
    }

    onSubmit({
      value: resetValue ? parseValue(resetValue) : null,
      currentlyPressedHotkey: null,
      shouldBlur: true,
    });
  };

  const getUpdatedValue = (
    clientX: number,
    clientY: number,
  ): number | string => {
    const { draggingType = "vertical", draggingDirection = "positive" } = props;
    const currentRefValue = currentValueRef.current || props.anchorValue || "0";

    const parsed = parseCommaSeparatedValues(currentRefValue);
    if (parsed) {
      const clientValue = draggingType === "horizontal" ? clientX : clientY;
      const snapshot =
        draggingType === "horizontal"
          ? lastDragStart?.x ?? 0
          : lastDragStart?.y ?? 0;
      let sign = draggingDirection === "negative" ? -1 : 1;
      if (draggingType === "horizontal") {
        sign *= -1;
      }
      const speed = props.dragSpeed ?? 1.5;
      const delta = sign * Math.floor((clientValue - snapshot) * speed);

      const minValue = minDragValues?.px ?? minValues?.px;
      const clampValue = (val: number) => {
        return minValue !== undefined ? Math.max(val, minValue) : val;
      };

      if (dragTargetIsFirstValue.current) {
        return formatCommaSeparatedValues(
          clampValue(parsed.first - delta),
          parsed.second,
        );
      }
      return formatCommaSeparatedValues(
        parsed.first,
        clampValue(parsed.second - delta),
      );
    }

    const value = Number.parseFloat(
      `${
        parseUnit(
          currentRefValue,
          props.unitDefaults ?? PARSE_UNIT_DEFAULTS,
          props.field,
          defaultUnit,
          props.resetValue,
          props.metrics,
          props.allowedNonUnitValues,
        ).value
      }`,
    );

    if (Number.isNaN(value)) {
      return currentValueRef.current;
    }
    const clientValue = draggingType === "horizontal" ? clientX : clientY;
    const snapshot =
      draggingType === "horizontal"
        ? lastDragStart?.x ?? 0
        : lastDragStart?.y ?? 0;
    let sign = draggingDirection === "negative" ? -1 : 1;
    if (draggingType === "horizontal") {
      sign *= -1;
    }
    const speed = props.dragSpeed ?? 1.5;
    return value - sign * Math.floor((clientValue - snapshot) * speed);
  };

  const onMouseDown = (event: React.MouseEvent) => {
    if (props.isDisabled) {
      return;
    }
    props.onDragStart?.();
    const { draggingDirection, draggingType } = props;

    const currentRefValue = currentValueRef.current || "";
    if (parseCommaSeparatedValues(currentRefValue)) {
      const cursorPosition = inputRef.current?.selectionStart ?? 0;
      dragTargetIsFirstValue.current = isCursorOnFirstValue(
        currentRefValue,
        cursorPosition,
      );
    }

    setIsMouseDown(true);
    setLastDragStart({ x: event.clientX, y: event.clientY });

    if (shouldInputDrag && draggingType) {
      setDragCursor(getCursor(draggingType, draggingDirection));
    }
  };

  const onDragEnd = () => {
    props.onDragEnd?.();
    inputRef.current?.focus();
  };

  const onMouseUp = useEffectEvent((event: MouseEvent) => {
    const isInputDraggable = shouldInputDrag && isDragging;
    const finishUpdatingState = () => {
      setDragCursor(null);
      setIsMouseDown(false);
      setIsDragging(false);
    };

    const hotkey = getHotKey(event);

    if (hotkey === "altKey" && !isDragging) {
      finishUpdatingState();
      resetValueFn();
      onDragEnd();
      return;
    }

    if (!isInputDraggable) {
      finishUpdatingState();
      onDragEnd();
      return;
    }
    finishUpdatingState();
    onSubmit({
      value: parseCommaSeparatedValues(inputValue)
        ? inputValue
        : parseValue(inputValue),
      currentlyPressedHotkey: hotkey,
      shouldBlur: true,
    });
    onDragEnd();
  });

  const onMouseMove = useEffectEvent((event: MouseEvent) => {
    if (!shouldInputDrag) {
      return;
    }
    const { clientX, clientY } = event;
    let updatedValue = getUpdatedValue(clientX, clientY);

    if (typeof updatedValue === "string") {
      const newParsed = parseCommaSeparatedValues(updatedValue);
      if (newParsed) {
        setInputValue(updatedValue);
        setIsDragging(true);
        return;
      }
    }

    if (Number.isNaN(Number.parseFloat(`${updatedValue}`))) {
      return;
    }

    if (!shouldAllowNegativeValue && (updatedValue as number) < 0) {
      updatedValue = 0;
    }
    // NOTE (Sebas, 2024-04-24): Previously we were using ?? here what caused the
    // preview to not work because we were using the wrong unit (an empty string)
    // instead of the unit from metrics.
    const unit = currentUnit || props.metrics?.[0];
    updatedValue = clampMinMax(
      updatedValue,
      maxDragValues?.[unit || ""],
      minDragValues?.[unit || ""],
    )!;

    const hotkey = getHotKey(event);
    const newValue = parseValue(
      updatedValue,
      currentUnit ?? props.metrics?.[0] ?? "px",
    );
    const value = Number.parseFloat(`${currentValueRef.current || 0}`);
    const difference = Math.abs(value - (updatedValue as number));
    setInputValue(newValue);
    setIsDragging(hotkey ? true : difference >= 1);
  });

  React.useEffect(() => {
    if (!isMouseDown) {
      return;
    }

    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
    return () => {
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [isMouseDown, onMouseMove, onMouseUp]);

  // Safety cleanup: reset drag cursor if component unmounts during drag
  React.useEffect(() => {
    return () => {
      setDragCursor(null);
    };
  }, []);

  return (
    <LengthInputSelectorContext.Provider
      value={{
        onMouseDown,
        draggingDirection: props.draggingDirection,
        dragTrigger: props.dragTrigger,
        draggingType: props.draggingType,
        field: props.field,
        isDisabled: props.isDisabled ?? false,
        isReadOnly: props.isReadOnly ?? false,
        onSubmit,
        inputRef,
        inputValue,
        setInputValue,
        parseValue,
        isMouseDown,
        metrics: props.metrics,
        allowedNonUnitValues: props.allowedNonUnitValues,
        unitDefaults: props.unitDefaults,
        defaultUnit,
        maxValues: props.maxValues,
        minValues: props.minValues,
        menuOptions: props.menuOptions,
        currentUnit: currentUnit,
        anchorValue: props.anchorValue,
        isFocused,
        setIsFocused,
        isDragging,
        isCurrentlySubmittingValueRef,
        resetValue: resetValueFn,
        isMixedStyleValue: isMixedStyleValue(props.value),
      }}
    >
      {props.children}
      {dragCursor
        ? ReactDOM.createPortal(
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                cursor: dragCursor,
                zIndex: 2_147_483_700,
              }}
            />,
            document.body,
          )
        : null}
    </LengthInputSelectorContext.Provider>
  );
};

// #endregion

// #region Draggable component

const LengthInputSelectorDraggable: React.FC<
  React.PropsWithChildren<{
    className?: string;
    dataTestId?: string;
  }>
> = ({ children, dataTestId, className }) => {
  const { onMouseDown, isDisabled } = useRequiredContext(
    LengthInputSelectorContext,
  );
  return (
    <div
      className={twMerge(
        "flex min-w-0 cursor-ns-resize",
        isDisabled && "cursor-not-allowed",
        className,
      )}
      onMouseDown={onMouseDown}
      data-testid={dataTestId ?? "draggable-component-wrapper"}
    >
      {children}
    </div>
  );
};

// #endregion

// #region ChevronMenuIndicator (inlined)

const ChevronMenuIndicator: React.FC<{
  menuOptions: InputOption[];
  displayValue: string | number;
  menuWidth?: number;
  isDisabled?: boolean;
  alignOffset?: number;
  sideOffset?: number;
  onOptionSelect: (args: { label: string; value: string | null }) => void;
}> = ({
  menuOptions,
  displayValue,
  menuWidth,
  isDisabled = false,
  alignOffset = 0,
  sideOffset = 0,
  onOptionSelect,
}) => (
  <Menu disabled={isDisabled}>
    <MenuTrigger
      render={
        <div className="flex h-4 w-4 items-center justify-center rounded-sm">
          <ChevronDown className="text-muted" size={12} />
        </div>
      }
    />
    <MenuContent
      align="end"
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      UNSAFE_className="min-w-0"
      style={{ width: menuWidth ? `${menuWidth}px` : undefined }}
    >
      {menuOptions.map((option) => {
        const isSelected =
          String(displayValue) === option.label ||
          (option.value !== null &&
            String(displayValue) === String(option.value));

        return (
          <MenuItem
            key={option.label}
            label={option.label}
            startEnhancer={option.icon}
            endEnhancer={isSelected ? <Check /> : null}
            disabled={option.isDisabled}
            selected={isSelected}
            onClick={() => {
              onOptionSelect({ label: option.label, value: option.value });
            }}
          />
        );
      })}
    </MenuContent>
  </Menu>
);

// #endregion

// #region Input component

const LengthInputSelectorInput: React.FC<
  Pick<LengthInputSelectorProps, LengthInputSelectorPropsNeededForInput> & {
    className?: string;
  }
> = (props) => {
  const {
    dragTrigger,
    draggingType,
    draggingDirection,
    isDisabled,
    isReadOnly,
    field,
    onSubmit,
    inputRef,
    isMouseDown,
    inputValue,
    setInputValue,
    parseValue,
    metrics,
    allowedNonUnitValues,
    unitDefaults,
    maxValues,
    minValues,
    menuOptions,
    defaultUnit,
    currentUnit,
    anchorValue,
    isFocused,
    setIsFocused,
    isDragging,
    isCurrentlySubmittingValueRef,
    resetValue,
  } = useRequiredContext(LengthInputSelectorContext);

  const displayValue = getDisplayValueFromMenuOptions({
    isRawInputFocused: isFocused,
    inputValue,
    menuOptions,
    unitDefaults,
    field,
    defaultUnit,
    metrics,
    allowedNonUnitValues,
  });

  const allowDragOnLabel = dragTrigger === "label";
  const shouldAllowDragOnStartEnhancer =
    !allowDragOnLabel &&
    ((!dragTrigger && Boolean(draggingType)) ||
      dragTrigger === "startEnhancer" ||
      (Boolean(props.startEnhancer) && dragTrigger !== "none"));

  const inputStyles: Record<string, string> = {
    backgroundColor: props.backgroundColor ?? "transparent",
    cursor: isReadOnly ? "pointer" : getCursor(draggingType, draggingDirection),
    caretColor: isMouseDown ? "transparent" : "auto",
  };

  const [inputContainerRef, { width }] = useMeasure();

  const _onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!["ArrowUp", "ArrowDown", "Enter"].includes(event.key)) {
      return;
    }
    if (event.key === "Enter") {
      const normalized = normalizeCommaSeparatedInput(inputValue);
      onSubmit({
        value:
          normalized ??
          (parseCommaSeparatedValues(inputValue)
            ? inputValue
            : parseValue(inputValue)),
        currentlyPressedHotkey: null,
        shouldBlur: true,
      });
      return;
    }

    const transform = (currentValue: number, valueToChange: number): number => {
      if (event.key === "ArrowDown") {
        return clampMinMax(
          currentValue - valueToChange,
          maxValues?.[currentUnit],
          minValues?.[currentUnit],
        )!;
      }
      return clampMinMax(
        currentValue + valueToChange,
        maxValues?.[currentUnit],
        minValues?.[currentUnit],
      )!;
    };

    const parsed = parseCommaSeparatedValues(inputValue);
    if (parsed) {
      const cursorPosition = inputRef.current?.selectionStart ?? 0;
      const isFirst = isCursorOnFirstValue(inputValue, cursorPosition);
      const newFirst = isFirst ? transform(parsed.first, 1) : parsed.first;
      const newSecond = isFirst ? parsed.second : transform(parsed.second, 1);
      const newValue = formatCommaSeparatedValues(newFirst, newSecond);

      setInputValue(newValue);
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
      });
      return;
    }

    let valueToStartFrom = Number.parseFloat(inputValue);
    let unitToIncrementBy = parseUnit(
      inputValue,
      unitDefaults ?? PARSE_UNIT_DEFAULTS,
      field,
      defaultUnit,
      undefined,
      metrics,
      allowedNonUnitValues,
    ).unit;

    if (!inputValue) {
      if (!anchorValue) {
        return;
      }
      const { value: anchorValueParsed, parsedUnit: anchorUnit } = parseUnit(
        anchorValue,
        unitDefaults ?? PARSE_UNIT_DEFAULTS,
        field,
        defaultUnit,
        undefined,
        metrics,
        allowedNonUnitValues,
      );
      const anchorValueNumber = Number.parseFloat(`${anchorValueParsed}`);

      if (!Number.isNaN(anchorValueNumber)) {
        valueToStartFrom = anchorValueNumber;
        unitToIncrementBy = anchorUnit ?? currentUnit;
      }
    }
    let newValue = "";
    if (["", "px", "%", "vw", "vh"].includes(unitToIncrementBy)) {
      const value = transform(valueToStartFrom, 1);
      newValue = parseValue(value, unitToIncrementBy);
    } else {
      const value = round(transform(valueToStartFrom, 0.1), 1);
      newValue = parseValue(value, unitToIncrementBy);
    }
    setInputValue(newValue);
  };

  const _onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isArrowKey = ["ArrowUp", "ArrowDown"].includes(event.key);
    const isDraggableUnit = CSS_LENGTH_TYPES.concat("").includes(currentUnit);
    const isHotkeyPressed = Boolean(getHotKey(event));

    if ((isArrowKey && isDraggableUnit) || (isHotkeyPressed && isDragging)) {
      onSubmit({
        value: inputValue,
        currentlyPressedHotkey: getHotKey(event),
        shouldBlur: false,
      });
    }
  };

  const startEnhancer = props.startEnhancer;

  const handleMenuOptionSelect = React.useCallback(
    ({ label, value: menuValue }: { label: string; value: string | null }) => {
      if (menuValue === null) {
        setInputValue(label);
        setIsFocused(false);
      }
      onSubmit({
        value: menuValue,
        currentlyPressedHotkey: null,
        shouldBlur: true,
      });
    },
    [setInputValue, setIsFocused, onSubmit],
  );

  const resolvedStartEnhancer =
    startEnhancer && shouldAllowDragOnStartEnhancer ? (
      <LengthInputSelectorDraggable dataTestId={`${field}-startEnhancer`}>
        {startEnhancer}
      </LengthInputSelectorDraggable>
    ) : (
      startEnhancer
    );

  const resolvedEndEnhancer = menuOptions ? (
    <ChevronMenuIndicator
      menuOptions={menuOptions}
      displayValue={displayValue}
      menuWidth={width}
      isDisabled={isDisabled}
      alignOffset={-8}
      sideOffset={8}
      onOptionSelect={handleMenuOptionSelect}
    />
  ) : (
    props.endEnhancer?.()
  );

  return (
    <LengthInputSelectorDraggable className="flex h-full w-full">
      <Input
        ref={inputRef}
        wrapperRef={inputContainerRef}
        size="sm"
        startEnhancer={resolvedStartEnhancer}
        endEnhancer={resolvedEndEnhancer}
        disabled={isDisabled}
        readOnly={isReadOnly}
        placeholder={props.placeholder ?? "auto"}
        value={displayValue}
        onPaste={(e) => {
          const pastedText = e.clipboardData.getData("text");
          const normalized = normalizeCommaSeparatedInput(pastedText);
          onSubmit({
            value:
              normalized ??
              (parseCommaSeparatedValues(pastedText)
                ? pastedText
                : parseValue(pastedText)),
            currentlyPressedHotkey: null,
            shouldBlur: true,
          });
        }}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        UNSAFE_className={twMerge(
          "focus-within:ring-0 focus-within:ring-transparent focus-within:border-component-border",
          "py-0 px-1.5 gap-1",
          isReadOnly ? "hover:bg-surface cursor-pointer" : "cursor-ns-resize",
          isDisabled && "cursor-not-allowed",
          props.className,
        )}
        style={inputStyles}
        onKeyDown={_onKeyDown}
        onKeyUp={_onKeyUp}
        onFocus={() => {
          if (!isDisabled) {
            setIsFocused(true);
            requestAnimationFrame(() => inputRef.current?.select());
          }
        }}
        onBlur={(e) => {
          e.preventDefault();
          setIsFocused(false);
          if (!isCurrentlySubmittingValueRef.current) {
            const normalized = normalizeCommaSeparatedInput(inputValue);
            onSubmit({
              value: normalized ?? parseValue(inputValue),
              currentlyPressedHotkey: null,
              shouldBlur: false,
            });
          }
        }}
        onClick={(e) => {
          if (
            getHotKey(e) === "altKey" &&
            !isDragging &&
            Boolean(props.startEnhancer)
          ) {
            resetValue();
          }
        }}
        data-testid={`${field}-input`}
        autoFocus={props.autofocus}
      />
    </LengthInputSelectorDraggable>
  );
};

// #endregion

// #region Main exported component

export const LengthInputSelector = (props: LengthInputSelectorProps) => {
  return (
    <LengthInputSelectorRoot {...props}>
      <div
        className={twMerge(
          "flex flex-row items-center justify-center min-w-0",
          props.className,
        )}
        style={props.style}
      >
        {props.label &&
          (props.dragTrigger === "label" ? (
            <LengthInputSelectorDraggable dataTestId={`${props.field}-label`}>
              {props.label}
            </LengthInputSelectorDraggable>
          ) : (
            props.label
          ))}
        <LengthInputSelectorInput
          className={props.inputClassName}
          startEnhancer={props.startEnhancer}
          endEnhancer={props.endEnhancer}
          placeholder={props.placeholder}
          autofocus={props.autofocus}
          backgroundColor={props.backgroundColor}
        />
      </div>
    </LengthInputSelectorRoot>
  );
};

LengthInputSelector.Root = LengthInputSelectorRoot;
LengthInputSelector.Input = LengthInputSelectorInput;
LengthInputSelector.DraggableArea = LengthInputSelectorDraggable;

// #endregion
